/**
 * soundSystem — zentrale, asset-freie Ruftöne über die Web Audio API (P3).
 *
 * Erzeugt Klingelton (eingehend) und Freizeichen/Ringback (ausgehend) rein
 * synthetisch (Oscillator + Gain) — KEINE Audio-Dateien, KEINE Dependency. Ein
 * einziges Singleton hält EINE laufende Tonquelle: `startRingtone()` und
 * `startRingback()` sind gegenseitig exklusiv, jeweils idempotent. `stop()`
 * beendet jede Wiedergabe.
 *
 * Lautstärke bewusst moderat (Klingelton) bzw. leiser (Ringback, soll das spätere
 * Gespräch nicht übertönen). Der AudioContext wird lazy beim ersten Start erzeugt
 * und — soweit die Browser-Policy es zulässt — aus `suspended` resumed:
 * - Ausgehend: Start erfolgt nach dem dial()-Klick (User-Geste) → resume klappt.
 * - Eingehend: Start kann ohne Geste passieren → best-effort; ein blockierter
 *   AudioContext bleibt einfach stumm, ohne den Anruf zu stören.
 *
 * Frequenzen/Kadenz (Annahmen):
 * - Klingelton: Zwei-Ton-Akkord 440 Hz + 480 Hz, 1,0 s an / 2,0 s aus (Periode 3 s),
 *   peak gain 0,09 — präsenter, „klassischer" Klingel-Charakter.
 * - Ringback (DE-Freizeichen): 425 Hz, 1,0 s an / 4,0 s aus (Periode 5 s),
 *   peak gain 0,05 — leiser als das Gespräch.
 */

import { alleFenster } from "../components/windowManager/browserFenster/fensterRegister";
type RingMode = "ringtone" | "ringback";

const FREIGABE_EREIGNISSE = ["pointerdown", "keydown", "touchstart"] as const;

interface ActiveNodes {
  oscillators: OscillatorNode[];
  gain: GainNode;
}

interface CadenceSpec {
  /** Trägerfrequenz(en) in Hz; mehrere = Akkord (bzw. Trill-Paar, s. warbleSec). */
  frequencies: number[];
  /** Spitzen-Lautstärke (0..1). */
  peak: number;
  /** Dauer der „an"-Phase in Sekunden (Standard-Burst, wenn `bursts` fehlt). */
  onSec: number;
  /** Gesamtperiode (an + aus) in Sekunden. */
  periodSec: number;
  /**
   * Mehrere „an"-Bursts je Periode (relativer Start `at` + Dauer `dur`, in s) — z. B.
   * Doppel-Ring „ring-ring … Pause". Fehlt das Feld, gibt es genau einen Burst der Länge onSec.
   */
  bursts?: { at: number; dur: number }[];
  /**
   * Wenn gesetzt: EIN Oszillator trillert im Burst alle `warbleSec` zwischen
   * frequencies[0] und frequencies[1] (klassischer Telefon-Klingel-Charakter, klar
   * unterscheidbar vom Dauer-Freizeichen). Sonst klingt frequencies als Akkord.
   */
  warbleSec?: number;
}

const CADENCES: Record<RingMode, CadenceSpec> = {
  // Eingehend: klassischer TELEFON-KLINGELTON — Doppel-Ring (ring-ring … Pause) mit
  // Trillern zwischen zwei Tönen. Bewusst anders als das Freizeichen (Dauerton), damit
  // „es klingelt jemand an" klar erkennbar ist (eher Handy-Klingel-Charakter).
  ringtone: {
    frequencies: [660, 524],
    // 08.10.2026: die Anhebung auf 0,3 vom Vortag wieder zurückgenommen. Zu
    // hören ist der Ton jetzt über das richtige Gerät (setzeKlingelGeraet).
    peak: 0.11,
    onSec: 0.4,
    periodSec: 3.0,
    bursts: [
      { at: 0.0, dur: 0.4 },
      { at: 0.6, dur: 0.4 },
    ],
    warbleSec: 0.1,
  },
  // Ausgehend: deutsches Freizeichen (425 Hz, 1 s an / 4 s aus), leiser.
  ringback: { frequencies: [425], peak: 0.05, onSec: 1.0, periodSec: 5.0 },
};

// Sanfte Flanken vermeiden Knackser beim Ein-/Ausblenden eines Burst.
const RAMP_SEC = 0.02;

/**
 * So viele Perioden werden im Voraus auf der Audio-Uhr geplant. Edge/Chrome
 * drosseln `setInterval` in Hintergrund-Tabs auf bis zu einmal je Minute — mit
 * nur EINER geplanten Periode klingelte es dann einmal und danach nicht mehr
 * (07.10.2026). Die Audio-Uhr läuft ungedrosselt weiter.
 */
const PERIODEN_VORAUS = 20;

class SoundSystem {
  private ctx: AudioContext | null = null;
  private mode: RingMode | null = null;
  private nodes: ActiveNodes | null = null;
  private cadenceTimer: number | null = null;
  private freigabeInstalliert = false;
  /** Ausgabegerät für den Klingelton ("" = Standard), s. klingelGeraet.ts. */
  private klingelSink = "";

  /**
   * Klingelton über dieses Ausgabegerät (08.10.2026). Das Freizeichen beim
   * Wählen bleibt auf dem Standard — dort läuft auch das Gespräch.
   */
  setzeKlingelGeraet(deviceId: string): void {
    this.klingelSink = deviceId;
    if (this.mode) this.wendeGeraetAn(this.mode);
  }

  /** Klingelton (eingehend) starten — stoppt einen evtl. laufenden Ringback. */
  startRingtone(): void {
    this.start("ringtone");
  }

  /** Ringback/Freizeichen (ausgehend) starten — stoppt einen evtl. laufenden Klingelton. */
  startRingback(): void {
    this.start("ringback");
  }

  /**
   * AudioContext SYNCHRON im User-Gesten-Fenster anlegen/resumen, OHNE Ton zu
   * starten. In softphone.dial()/answer() aufrufen (analog zum Audio-Element-Prime
   * via playAudio('dial-gesture')): startRingback() läuft erst Sekunden später im
   * async SDK-Event, also außerhalb jeder Geste — strikte Autoplay-Browser
   * (v. a. Safari/iOS) lassen resume() aber nur synchron in der Geste zu. Ohne
   * dieses Prime bliebe das ausgehende Ringback dort dauerhaft stumm.
   */
  prime(): void {
    this.ensureCtx();
  }

  /**
   * Den AudioContext beim ERSTEN Klick bzw. Tastendruck auf der Seite anlegen
   * (07.10.2026). Ein eingehender Anruf kommt ohne Geste; Edge lässt einen
   * dann erst angelegten Context stumm („suspended"). Einmal in einer Geste
   * gestartet, darf er später ohne Geste klingeln. Idempotent.
   */
  freigabeBeiBedienung(): void {
    if (this.freigabeInstalliert || typeof window === "undefined") return;
    this.freigabeInstalliert = true;
    const los = () => {
      const ctx = this.ensureCtx();
      if (ctx && ctx.state === "running") {
        for (const ev of FREIGABE_EREIGNISSE) alleFenster.removeEventListener(ev, los, true);
      }
    };
    for (const ev of FREIGABE_EREIGNISSE) alleFenster.addEventListener(ev, los, true);
  }

  /** Läuft der Ton wirklich (nicht vom Browser stummgehalten)? Für die Diagnose. */
  istHoerbar(): boolean {
    return !!this.ctx && this.ctx.state === "running";
  }

  /** Jede laufende Wiedergabe beenden (idempotent). */
  stop(): void {
    if (this.cadenceTimer !== null) {
      window.clearInterval(this.cadenceTimer);
      this.cadenceTimer = null;
    }
    const active = this.nodes;
    this.nodes = null;
    this.mode = null;
    if (!active || !this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const param = active.gain.gain;
    // Aktuellen (ggf. mitten in einer Rampe liegenden) Wert halten und sauber
    // gegen 0 ausblenden, damit das Stoppen nicht knackt.
    const holdable = param as AudioParam & {
      cancelAndHoldAtTime?: (t: number) => void;
    };
    if (typeof holdable.cancelAndHoldAtTime === "function") {
      holdable.cancelAndHoldAtTime(t);
    } else {
      param.cancelScheduledValues(t);
    }
    param.linearRampToValueAtTime(0, t + 0.03);
    for (const osc of active.oscillators) {
      try {
        osc.stop(t + 0.05);
      } catch {
        /* bereits gestoppt */
      }
    }
    // Knoten nach dem Ausklingen trennen (vermeidet das Anhäufen toter Nodes).
    window.setTimeout(() => {
      try {
        active.gain.disconnect();
      } catch {
        /* ignore */
      }
      for (const osc of active.oscillators) {
        try {
          osc.disconnect();
        } catch {
          /* ignore */
        }
      }
    }, 80);
  }

  private ensureCtx(): AudioContext | null {
    if (typeof window === "undefined") return null;
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume().catch(() => {
        /* best-effort: ohne Geste kann der Browser das ablehnen → bleibt stumm */
      });
    }
    return this.ctx;
  }

  /** Den Context auf das Gerät des Tons umstellen (Edge/Chrome ab 110; sonst Standard). */
  private wendeGeraetAn(mode: RingMode): void {
    const ctx = this.ctx as (AudioContext & { setSinkId?: (id: string) => Promise<void>; sinkId?: unknown }) | null;
    if (!ctx || typeof ctx.setSinkId !== "function") return;
    const ziel = mode === "ringtone" ? this.klingelSink : "";
    const jetzt = typeof ctx.sinkId === "string" ? ctx.sinkId : "";
    if (jetzt === ziel) return;
    void ctx.setSinkId(ziel).catch((err: unknown) => {
      console.warn("[telephony] Klingelton-Gerät nicht wählbar — es bleibt der Standard", err);
    });
  }

  private start(mode: RingMode): void {
    // Bereits derselbe Ton aktiv: nur den Context (re)aktivieren, NICHT neu
    // aufbauen → idempotent gegen Re-Renders/Re-Entrancy, Kadenz läuft weiter.
    if (this.mode === mode) {
      this.ensureCtx();
      return;
    }
    // Anderer Ton lief: erst stoppen (nur EINE Quelle gleichzeitig).
    this.stop();
    const ctx = this.ensureCtx();
    if (!ctx) return;
    this.wendeGeraetAn(mode);
    const spec = CADENCES[mode];
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(ctx.destination);
    const oscillators: OscillatorNode[] = [];
    // Trill (warbleSec): EIN Oszillator, dessen Frequenz im Burst gewechselt wird.
    // Sonst: ein Oszillator je Frequenz (Akkord, z. B. Freizeichen).
    const freqs = spec.warbleSec !== undefined ? [spec.frequencies[0]] : spec.frequencies;
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      osc.connect(gain);
      osc.start();
      oscillators.push(osc);
    }
    this.nodes = { oscillators, gain };
    this.mode = mode;
    if (ctx.state !== "running") {
      // Der Browser hält den Ton zurück (noch keine Bedienung auf der Seite).
      // Steht in den Telefonie-Spuren des Tickets.
      console.warn(`[telephony] Klingelton vom Browser stummgehalten (AudioContext ${ctx.state})`);
    }
    // Erste Periode sofort planen, danach periodisch nachlegen.
    this.schedulePeriod(spec);
    this.cadenceTimer = window.setInterval(() => {
      // Manche Browser suspendieren den Context im Hintergrund — best-effort
      // wiederbeleben, dann die nächste Periode planen.
      if (this.ctx?.state === "suspended") {
        void this.ctx.resume().catch(() => {
          /* ignore */
        });
      }
      this.schedulePeriod(spec);
    }, spec.periodSec * 1000);
  }

  /**
   * Ab jetzt `PERIODEN_VORAUS` Perioden planen: je einen ODER mehrere „an"-Bursts (weiche Flanken),
   * optional mit Frequenz-Trill innerhalb jedes Bursts (Telefon-Klingel-Charakter).
   */
  private schedulePeriod(spec: CadenceSpec): void {
    const ctx = this.ctx;
    const nodes = this.nodes;
    if (!ctx || !nodes) return;
    const param = nodes.gain.gain;
    const t0 = ctx.currentTime;
    param.cancelScheduledValues(t0);
    param.setValueAtTime(0, t0);
    nodes.oscillators[0]?.frequency.cancelScheduledValues(t0);
    const bursts = spec.bursts ?? [{ at: 0, dur: spec.onSec }];
    for (let p = 0; p < PERIODEN_VORAUS; p++) {
      for (const b of bursts) {
        const start = t0 + p * spec.periodSec + b.at;
        param.setValueAtTime(0, start);
        param.linearRampToValueAtTime(spec.peak, start + RAMP_SEC);
        param.setValueAtTime(spec.peak, start + b.dur - RAMP_SEC);
        param.linearRampToValueAtTime(0, start + b.dur);
        // Trill: Frequenz des (einzigen) Oszillators im Burst zwischen [0]/[1] wechseln.
        if (spec.warbleSec !== undefined && spec.frequencies.length >= 2) {
          const osc = nodes.oscillators[0];
          let high = true;
          for (let tt = 0; tt < b.dur - 1e-4; tt += spec.warbleSec) {
            osc.frequency.setValueAtTime(high ? spec.frequencies[0] : spec.frequencies[1], start + tt);
            high = !high;
          }
        }
      }
    }
  }
}

/** Prozessweit ein einziges Sound-System (eine Tonquelle gleichzeitig). */
export const soundSystem = new SoundSystem();
